import { TypeKeys } from '.';
import '@sworkit/capacitor-feed-fm';
import { FeedFm, FeedFMExtraOptions } from '@sworkit/capacitor-feed-fm';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { tokens } from '../tokens';
import { getLocalAsset } from '../global/native';
import { ModalOptions, modalController } from '@ionic/core';
import { FeedFMPlayerEvent } from '@sworkit/capacitor-feed-fm';
import { setAudioDucking } from './local-settings';

registerPlugin('FeedFM');

interface PlayStartedEvent {
  id: string;
  station: {
    id: string;
    name: string;
  };
  audio_file: {
    id: string;
    duration_in_seconds: number;
    track: {
      id: string;
      title: string;
    };
    release: {
      id: string;
      title: string;
    };
    artist: {
      id: string;
      name: string;
    };
  };
}

export interface MusicInit {
  type: TypeKeys.MUSIC_INIT;
}

export const initMusic = () => async (dispatch, _getState) => {
  const {
    musicPlayer: { musicInitialized },
    ui: { online },
  } = _getState() as SworkitState;
  if (musicInitialized || !online) return;
  await initFeedFM();
  const action: MusicInit = {
    type: TypeKeys.MUSIC_INIT,
  };
  dispatch(action);
  FeedFm.tune();
  FeedFm.on(feedEvent('all'), (callbackObject) => {
    console.debug('player-event: ', callbackObject.event || callbackObject);
  }
  );
  FeedFm.on(feedEvent('play-active'), () => {
    if (Capacitor.isNativePlatform()) {
      //@todo: this dispatch breaks web, check to see if we need on native
      dispatch(playMusicAction());

    }
  });
  FeedFm.on(feedEvent('play-resumed'), () => dispatch(playMusicAction()));
  FeedFm.on(feedEvent('play-paused'), () => dispatch(pauseMusicAction()));
  FeedFm.on(feedEvent('stations'), (callbackObject: any) => {
    const stations: FeedFMstation[] = callbackObject.event || callbackObject;
    dispatch(setFeedFMStations(stations));
  });
  dispatch(setInitialMusicVolume());
  FeedFm.on(feedEvent('play-started'), (event: PlayStartedEvent) => {
    // console.debug('play started event', event)
    if (event) {
      const musicPlayerState: Partial<MusicPlayerState> = {};
      if (event.station) {
        musicPlayerState.stationName = event.station.name;
        musicPlayerState.activeStationId = event.station.id;
      }
      if (event.audio_file) {
        musicPlayerState.songName = event.audio_file.track.title;
        musicPlayerState.artistName =
          event.audio_file.artist && event.audio_file.artist.name
            ? event.audio_file.artist.name
            : null;
        musicPlayerState.albumName = event.audio_file.release
          ? event.audio_file.release.title
          : null;
      }
      dispatch(updateMusicPlayerState(musicPlayerState));
    }
  });
};

function initFeedFM() {
  const extraOptions: FeedFMExtraOptions = {
    crossFadeIn: true,
    secondsOfCrossfade: 3,
    normalizeVolume: true,
  };

  const player = FeedFm.newPlayer({
    feedFMtoken: tokens.feedFMtoken,
    feedFMsecret: tokens.feedFMsecret,
    extraOptions,
  });
  const webPlayer = !Capacitor.isNativePlatform();
  if (webPlayer) {
    window['player'] = player;
  }
  return player;
}

function feedEvent(str: FeedFMPlayerEvent) {
  return { event: str };
}

export interface MusicPlay {
  type: TypeKeys.MUSIC_PLAY;
}

function playMusicAction() {
  const action: MusicPlay = {
    type: TypeKeys.MUSIC_PLAY,
  };
  return action;
}

export const playMusic = () => async (dispatch, _getState) => {
  FeedFm.initializeAudio();
  FeedFm.play();
  dispatch(playMusicAction());
};

export interface MusicVolume {
  type: TypeKeys.MUSIC_VOLUME;
  musicVolume: number;
}

const setInitialMusicVolume = () => async (dispatch, _getState) => {
  const state = _getState() as SworkitState;
  let {
    localSettings: { musicVolume },
  } = state;
  if ((!musicVolume || isNaN(musicVolume)) && musicVolume != 0) {
    musicVolume = 100;
  }
  const action: MusicVolume = {
    type: TypeKeys.MUSIC_VOLUME,
    musicVolume,
  };
  dispatch(action);
  FeedFm.setVolume({ volume: musicVolume });
};

export const adjustMusicVolume = (musicVolume: number) => async (
  dispatch,
  _getState
) => {
  const action: MusicVolume = {
    type: TypeKeys.MUSIC_VOLUME,
    musicVolume,
  };
  dispatch(action);
  FeedFm.setVolume({ volume: musicVolume });
};

export interface MusicPause {
  type: TypeKeys.MUSIC_PAUSE;
}

function pauseMusicAction() {
  const action: MusicPause = {
    type: TypeKeys.MUSIC_PAUSE,
  };
  return action;
}

export const pauseMusic = () => async (_dispatch, _getState) => {
  FeedFm.pause();
};

export interface MusicChangeStation {
  type: TypeKeys.MUSIC_CHANGE_STATION;
  stationId: string;
}

export const changeMusicStation = (stationId: string) => async (
  dispatch,
  _getState
) => {
  const {
    musicPlayer: { isPlaying, activeStationId },
  } = _getState() as SworkitState;
  const action: MusicChangeStation = {
    type: TypeKeys.MUSIC_CHANGE_STATION,
    stationId,
  };
  dispatch(action);
  FeedFm.setStationId({ stationId });
  if (!isPlaying) {
    dispatch(playMusic());
  } else if (activeStationId === stationId) {
    dispatch(pauseMusic());
  }
};

export interface MusicSkip {
  type: TypeKeys.MUSIC_SKIP;
}

export const skipMusic = () => async (dispatch, _getState) => {
  const action: MusicSkip = {
    type: TypeKeys.MUSIC_SKIP,
  };
  dispatch(action);
  FeedFm.skip();
};

export interface MusicUpdateState {
  type: TypeKeys.MUSIC_UPDATE_STATE;
  musicPlayerState: Partial<MusicPlayerState>;
}

export const updateMusicPlayerState = (
  musicPlayerState: Partial<MusicPlayerState>
) => async (dispatch, _getState) => {
  const {
    workoutPlayer: { isPlaying },
    musicPlayer: { shouldStopMusicAfterSong },
  } = _getState() as SworkitState;

  if (!isPlaying && shouldStopMusicAfterSong) {
    // if workout has ended - don't play next song
    console.debug('workout has ended - stop playing music');
    dispatch(pauseMusic());
  } else {
    // workout is playing - keep playing music
    console.debug('workout is playing - keep playing music');
  }
  const action: MusicUpdateState = {
    type: TypeKeys.MUSIC_UPDATE_STATE,
    musicPlayerState,
  };
  return dispatch(action);
};

const setFeedFMStations = (stations: FeedFMstation[]) => async (
  dispatch,
  getState
) => {
  const promises = stations.map(async (station) => {
    const image = await getLocalAsset(
      `https://storage.googleapis.com/sworkit-assets/images/music/${station.options.s_id ? station.options.s_id : station.options.stationID}.png`
    );
    station.image = image;
    return station;
  });
  const stationsWithImage: FeedFMstation[] = await Promise.all(promises);
  const {
    subscription: { source },
  } = getState() as SworkitState;
  if (source === 'sworkit-youth') {
    const onlyKids = stationsWithImage.filter((station) =>
      isKidsStation(station)
    );
    dispatch(setFeedFMStationsAction(onlyKids));
  } else {
    dispatch(setFeedFMStationsAction(stationsWithImage));
  }
};

export interface MusicSetFeedFMStations {
  type: TypeKeys.MUSIC_SET_FEEDFM_STATIONS;
  stations: FeedFMstation[];
}

function setFeedFMStationsAction(stations: FeedFMstation[]) {
  const action: MusicSetFeedFMStations = {
    type: TypeKeys.MUSIC_SET_FEEDFM_STATIONS,
    stations,
  };
  return action;
}

export async function showMusicPlaylist(
  collectionSlug: string,
  workoutSlug: string
) {
  const modalOptions: ModalOptions = {
    backdropDismiss: false,
    component: 'sw-music-playlist-page',
    componentProps: {
      workoutSlug,
      collectionSlug,
      isModal: true
    },
  };
  const modal = await modalController.create(modalOptions);
  modal.present();
}

export interface MusicStop {
  type: TypeKeys.MUSIC_STOP;
}

export const stopMusic = () => async (dispatch, _getState) => {
  const {
    musicPlayer: { musicInitialized },
    localSettings: { duckBackgroundAudio },
  } = _getState() as SworkitState;
  if (!musicInitialized) return;
  await FeedFm.stop();
  const action: MusicStop = {
    type: TypeKeys.MUSIC_STOP,
  };
  await dispatch(action);
  await dispatch(setAudioDucking(duckBackgroundAudio));
};

export function isKidsStation(station: FeedFMstation) {
  return (
    station.options &&
    station.options.s_id &&
    station.options.s_id.includes('kids')
  );
}
